import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerOverlay,
  Flex,
  Input,
  Spinner,
  Text,
  useDisclosure,
} from "@chakra-ui/react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { FiChevronDown, FiEye, FiSearch } from "react-icons/fi";
import moment from "moment";
import { AppBar, APP } from "../../components/mobileUi";
import {
  DesktopBack,
  DesktopPage,
  DesktopPanel,
  useIsDesktop,
} from "../../components/desktopUi";
import { DetailTabBar, DetailRow } from "../../components/financeDetail";
import { GradientButton } from "./trainingUi";
import {
  DOCUMENT_TYPE,
  DOCUMENT_TYPE_FILTER_OPTIONS,
  DOCUMENT_TYPE_LABEL,
  LESSON_TABS,
  htmlToPlainText,
} from "./trainingConstants";
import {
  getTrainingLessonDetailApi,
  getTrainingLessonDocumentsApi,
  getTrainingQuizHistoryApi,
} from "../../api/training.api";
import { EMP_BASE_PATH } from "../../config";

const TEXT = "#18212F";
const MUTED = "#94A3B8";
const BORDER = "#E1E1E1";
const LINE = "#EEF1F6";

const fmtDate = (v) => (v ? moment(v).format("DD-MM-YYYY") : "");

const scoreColor = (score) =>
  score >= 80 ? "#0BA859" : score >= 50 ? "#F79D0D" : "#EF2E14";

/* ------------------------------------------------ Thông tin chung */
const InfoTab = ({ detail, topicName, courseName }) => (
  <Box px="16px" pt="8px">
    <DetailRow label="Chủ đề" value={topicName} />
    <DetailRow label="Khoá học" value={courseName} />
    <DetailRow label="Tên bài học" value={detail?.name} />
    <DetailRow
      label="Mô tả"
      value={detail?.description ? htmlToPlainText(detail.description) : ""}
    />
    <DetailRow label="Ngày tạo" value={fmtDate(detail?.createdAt)} />
    <DetailRow label="Ngày cập nhật" value={fmtDate(detail?.updatedAt)} last />
  </Box>
);

/* ------------------------------------------------ Tài liệu */
const DocumentsTab = ({
  documents,
  loading,
  keyword,
  onChangeKeyword,
  docType,
  onOpenTypeSheet,
  onView,
}) => (
  <Box px="16px" pt="12px">
    <Flex gap="10px">
      <Flex
        flex="1"
        h="44px"
        align="center"
        px="12px"
        bg="white"
        border="1px solid"
        borderColor={BORDER}
        borderRadius="10px"
      >
        <Box as={FiSearch} color={MUTED} fontSize="18px" flexShrink={0} />
        <Input
          variant="unstyled"
          ml="8px"
          fontSize="14px"
          placeholder="Tìm kiếm tên tệp..."
          value={keyword}
          onChange={(e) => onChangeKeyword(e.target.value)}
        />
      </Flex>

      <Flex
        as="button"
        type="button"
        onClick={onOpenTypeSheet}
        w="130px"
        h="44px"
        align="center"
        justify="space-between"
        px="12px"
        bg="white"
        border="1px solid"
        borderColor={BORDER}
        borderRadius="10px"
        flexShrink={0}
      >
        <Text fontSize="14px" color={TEXT} noOfLines={1}>
          {DOCUMENT_TYPE_LABEL[docType]}
        </Text>
        <Box as={FiChevronDown} color={MUTED} fontSize="16px" />
      </Flex>
    </Flex>

    <Box h="8px" />

    {loading && !documents.length ? (
      <Flex justify="center" py="24px">
        <Spinner size="sm" color={APP.primary200} />
      </Flex>
    ) : documents.length ? (
      documents.map((doc) => (
        <Flex
          key={doc._id}
          align="center"
          py="12px"
          borderBottom="1px solid"
          borderColor="#F1F4F9"
        >
          <Box flex="1" pr="8px">
            <Text fontSize="14px" color={TEXT} noOfLines={3}>
              {doc.name}{" "}
              <Text as="span" fontSize="13px" color={MUTED}>
                ({DOCUMENT_TYPE_LABEL[doc.type] || doc.type})
              </Text>
            </Text>
          </Box>
          <Flex
            as="button"
            type="button"
            onClick={() => onView(doc)}
            w="40px"
            h="36px"
            align="center"
            justify="center"
            border="1px solid"
            borderColor={BORDER}
            borderRadius="8px"
            flexShrink={0}
            color={TEXT}
          >
            <Box as={FiEye} fontSize="18px" />
          </Flex>
        </Flex>
      ))
    ) : (
      <Flex justify="center" py="24px">
        <Text fontSize="14px" color={MUTED}>
          Không có tệp đính kèm
        </Text>
      </Flex>
    )}
  </Box>
);

/* ------------------------------------------------ Câu hỏi */
const QuestionsTab = ({ history, onStartQuiz }) => (
  <Box px="16px" pt="12px">
    <GradientButton h="48px" onClick={onStartQuiz}>
      Làm bài trắc nghiệm
    </GradientButton>

    <Box h="20px" />

    <Flex
      bg="#F1F5FB"
      py="10px"
      px="8px"
      borderTopRadius="8px"
      fontSize="13px"
      fontWeight={600}
      color={TEXT}
    >
      <Text flex="0.4">Số câu đúng</Text>
      <Text flex="0.25">Điểm</Text>
      <Text flex="0.35">Ngày nộp</Text>
    </Flex>

    {history.length ? (
      history.map((row) => (
        <Flex
          key={row._id}
          align="center"
          py="12px"
          px="8px"
          borderBottom="1px solid"
          borderColor={LINE}
          fontSize="13px"
        >
          <Text flex="0.4" color={TEXT}>
            {row.correctCount} / {row.totalQuestions}
          </Text>
          <Text flex="0.25" fontWeight={600} color={scoreColor(row.score)}>
            {row.score}/100
          </Text>
          <Text flex="0.35" color={TEXT}>
            {row.submittedAt ? moment(row.submittedAt).format("DD/MM/YYYY") : "-"}
          </Text>
        </Flex>
      ))
    ) : (
      <Flex justify="center" py="24px">
        <Text fontSize="14px" color={MUTED}>
          Chưa có lịch sử nộp bài
        </Text>
      </Flex>
    )}
  </Box>
);

const LessonDetail = () => {
  const isDesktop = useIsDesktop();
  const navigate = useNavigate();
  const { lessonId } = useParams();
  const location = useLocation();
  const topicName = location.state?.topicName || "";
  const courseName = location.state?.courseName || "";

  const [tab, setTab] = useState(1); // mặc định: Tài liệu (khớp app)
  const [detail, setDetail] = useState(null);
  const [history, setHistory] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [docLoading, setDocLoading] = useState(false);
  const [docKeyword, setDocKeyword] = useState("");
  const [docType, setDocType] = useState(DOCUMENT_TYPE.ALL);

  const typeSheet = useDisclosure();

  // Chi tiết bài học (API này đồng thời ghi 1 lượt xem ở BE) + lịch sử nộp bài.
  useEffect(() => {
    if (!lessonId) return;
    getTrainingLessonDetailApi(lessonId)
      .then(setDetail)
      .catch(() => setDetail(null));
    getTrainingQuizHistoryApi(lessonId)
      .then((d) => setHistory(Array.isArray(d) ? d : d?.items || []))
      .catch(() => setHistory([]));
  }, [lessonId]);

  const fetchDocuments = useCallback(async () => {
    if (!lessonId) return;
    setDocLoading(true);
    try {
      const params = {
        keyword: docKeyword.trim() || undefined,
        type: docType !== DOCUMENT_TYPE.ALL ? docType : undefined,
      };
      const data = await getTrainingLessonDocumentsApi(lessonId, params);
      setDocuments(Array.isArray(data) ? data : data?.items || []);
    } catch {
      setDocuments([]);
    } finally {
      setDocLoading(false);
    }
  }, [lessonId, docKeyword, docType]);

  // Debounce tìm kiếm; đổi loại tài liệu -> tải lại (khớp app).
  useEffect(() => {
    const t = setTimeout(fetchDocuments, 300);
    return () => clearTimeout(t);
  }, [fetchDocuments]);

  const onView = useCallback(
    (doc) => {
      const q = new URLSearchParams({
        name: doc.name || "Tài liệu",
        type: doc.type || DOCUMENT_TYPE.OTHER,
      }).toString();
      navigate(
        `${EMP_BASE_PATH}/dao-tao/bai-hoc/${lessonId}/tai-lieu/${doc._id}?${q}`
      );
    },
    [navigate, lessonId]
  );

  const onStartQuiz = useCallback(() => {
    navigate(`${EMP_BASE_PATH}/dao-tao/bai-hoc/${lessonId}/trac-nghiem`);
  }, [navigate, lessonId]);

  const activeKey = useMemo(() => LESSON_TABS[tab]?.key, [tab]);

  const pageBody = (
    <>

      <DetailTabBar tabs={LESSON_TABS} index={tab} onChange={setTab} />

      {activeKey === "INFO" && (
        <InfoTab detail={detail} topicName={topicName} courseName={courseName} />
      )}
      {activeKey === "DOCUMENT" && (
        <DocumentsTab
          documents={documents}
          loading={docLoading}
          keyword={docKeyword}
          onChangeKeyword={setDocKeyword}
          docType={docType}
          onOpenTypeSheet={typeSheet.onOpen}
          onView={onView}
        />
      )}
      {activeKey === "QUESTION" && (
        <QuestionsTab history={history} onStartQuiz={onStartQuiz} />
      )}

      {/* Bottom sheet chọn loại tài liệu */}
      <Drawer
        isOpen={typeSheet.isOpen}
        onClose={typeSheet.onClose}
        placement="bottom"
      >
        <DrawerOverlay bg="rgba(0,0,0,0.5)" />
        <DrawerContent borderTopRadius="16px" maxW="480px" mx="auto">
          <DrawerBody py="16px" px="16px">
            <Text fontSize="16px" fontWeight={700} color={TEXT} mb="4px">
              Loại tài liệu
            </Text>
            <Box maxH="50vh" overflowY="auto">
              {DOCUMENT_TYPE_FILTER_OPTIONS.map((opt, i) => {
                const active = opt.key === docType;
                const last = i === DOCUMENT_TYPE_FILTER_OPTIONS.length - 1;
                return (
                  <Flex
                    key={opt.key}
                    as="button"
                    w="100%"
                    align="center"
                    justify="space-between"
                    py="12px"
                    borderBottom={last ? "none" : "1px solid #EEF1F6"}
                    onClick={() => {
                      setDocType(opt.key);
                      typeSheet.onClose();
                    }}
                  >
                    <Text
                      fontSize="15px"
                      color={active ? APP.primary200 : TEXT}
                      fontWeight={active ? 600 : 400}
                    >
                      {opt.label}
                    </Text>
                  </Flex>
                );
              })}
            </Box>
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </>
  );

  // ===================== Bản WEB (desktop) =====================
  if (isDesktop) {
    return (
      <DesktopPage title="Chi tiết bài học" right={<DesktopBack onClick={() => navigate(-1)} />}>
        <DesktopPanel p="0" pb="24px">
          {pageBody}
        </DesktopPanel>
      </DesktopPage>
    );
  }

  return (
    <Box maxW="480px" mx="auto" pb="40px">
      <AppBar title="Chi tiết bài học" />
      {pageBody}
    </Box>
  );
};

export default LessonDetail;
